import React, { useState } from 'react';
import { Box, Paper, TextField, Tooltip, Typography } from '@mui/material';
import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { PageRow, PageSearch, PageUser, filterPages, pageName } from '../model';

const accessSentence = ({ name, days, last_visited: lastVisited }: PageUser): string =>
  days === 1
    ? `${name} accessed it on 1 day, ${lastVisited}.`
    : `${name} accessed it on ${days} different days, the last one on ${lastVisited}.`;

// DataTable clones this element for each expanded row and injects that row as `data` (see DataTable/ExpanderRow).
const PageUsers: React.FC<{ data?: PageRow }> = ({ data }) => (
  <Box component="ul" sx={{ m: 0, py: 1, pl: 8 }}>
    {data?.users.map((user) => <li key={user.id}>{accessSentence(user)}</li>)}
  </Box>
);

const columns = [
  { name: 'Module', selector: 'module', sortable: true, grow: 1 },
  { name: 'Page', selector: pageName, sortable: true, wrap: true, grow: 3 },
  { name: 'Users', selector: 'unique_users', sortable: true, grow: 0.5 },
  {
    name: (
      // describeChild keeps "User-days" as the header's accessible name; the explanation becomes its description.
      <Tooltip
        describeChild
        title="Days each user opened this page, added up. One user on 4 days plus another on 1 day = 5."
      >
        <span>User-days</span>
      </Tooltip>
    ),
    selector: 'user_days',
    sortable: true,
    grow: 0.5,
  },
  // Not sortable: MM/DD/YYYY strings sort wrongly as text, and every row falls inside the window anyway.
  { name: 'Last visited', selector: 'last_visited', grow: 1 },
];

const PagesTable: React.FC<{ pages: PageRow[] }> = ({ pages }) => {
  const [search, setSearch] = useState<PageSearch>({ page: '', module: '' });
  const searching = Boolean(search.page.trim() || search.module.trim());
  const visiblePages = filterPages(pages, search);

  return (
    <Paper component="section" aria-label="Pages" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6">Pages</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <TextField
            size="small"
            label="Search module"
            value={search.module}
            onChange={(e) => setSearch({ ...search, module: e.target.value })}
          />
          <TextField
            size="small"
            label="Search page"
            value={search.page}
            onChange={(e) => setSearch({ ...search, page: e.target.value })}
          />
        </Box>
      </Box>
      <DataTable
        columns={columns}
        data={visiblePages}
        keyField="path"
        defaultSortField="unique_users"
        defaultSortAsc={false}
        expandableRows
        expandableRowsComponent={<PageUsers />}
        pagination
        paginationPerPage={25}
        paginationRowsPerPageOptions={[25, 50, 100]}
        noHeader
        striped
        highlightOnHover
        noDataComponent={
          <Box sx={{ p: 3 }}>{searching ? 'No pages match your search' : 'No pages visited in this window'}</Box>
        }
      />
    </Paper>
  );
};

export default PagesTable;
